/**
 * Centralized Error Handler
 *
 * This module provides a centralized place to interpret errors and decide
 * what to do with them (show to user, log, navigate, etc.)
 *
 * Side effects (setError, navigate, log) are injected for testability.
 *
 * CRITICAL: All errors preserve their full chain and stack traces for debugging.
 * In development/test, full details are shown. In production, safe messages are shown
 * but details are logged for debugging.
 */

import type { AppError } from "../types/errors";
import { formatErrorForDisplay, getErrorDetails } from "../types/errors";
import { assertNever } from "./assertNever";
import { logger } from "./logger";

/**
 * Dependencies for error handler
 * All side effects are injected for testability
 */
export interface ErrorHandlerDeps {
  setError: (message: string) => void;
  navigate?: (path: string) => void;
  logError?: (error: AppError) => void;
  /** When true, shows full error details for debugging. Defaults to import.meta.env.VITE_SHOW_ERROR_DETAILS. */
  showErrorDetails?: boolean;
}

/**
 * Map Firebase error codes to user-friendly messages
 */
function mapFirebaseErrorToUserMessage(code: string): string {
  switch (code) {
    // Auth errors
    case "auth/popup-closed-by-user":
      return "Sign-in cancelled. Please try again.";
    case "auth/popup-timeout":
      return "Sign-in cancelled. Please try again.";
    case "auth/popup-blocked":
      return "Pop-up was blocked by your browser. Please allow pop-ups and try again.";
    case "auth/cancelled-popup-request":
      return "Sign-in cancelled. Please try again.";
    case "auth/network-request-failed":
      return "Unable to connect. Please check your internet connection.";
    case "auth/too-many-requests":
      return "Too many sign-in attempts. Please wait 60 seconds.";
    case "auth/user-disabled":
      return "This account has been disabled. Please contact support.";
    case "auth/account-exists-with-different-credential":
      return "An account with this email already exists. Please use your original sign-in method.";

    // Firestore errors
    case "permission-denied":
      return "Access denied. You do not have permission to access this data.";
    case "unavailable":
      return "Service temporarily unavailable. Please try again in a few moments.";
    case "deadline-exceeded":
      return "Request timed out. Please check your connection and try again.";
    case "not-found":
      return "Data not found. It may have been deleted or moved.";
    case "already-exists":
      return "This data already exists. Please use a different identifier.";
    case "resource-exhausted":
      return "Too many requests. Please wait a moment and try again.";
    case "failed-precondition":
      return "Invalid request. The data does not meet requirements.";
    case "aborted":
      return "Operation cancelled. Please try again.";
    case "out-of-range":
      return "Invalid data range. Please check your input and try again.";
    case "unimplemented":
      return "This feature is not yet available. Please try again later.";
    case "internal":
      return "Internal server error. Please try again or contact support.";
    case "data-loss":
      return "Critical data error detected. Please contact support immediately.";
    case "unauthenticated":
      return "Authentication required. Please sign in again.";

    default:
      return `An error occurred (${code}). Please try again.`;
  }
}

/**
 * Create an error handler with injected dependencies
 *
 * IMPORTANT: In development/test mode, full error details including stack traces
 * are shown to users. In production, safe messages are shown but full details are logged.
 *
 * @example
 * const handleError = createErrorHandler({
 *   setError: (msg) => setErrorState(msg),
 *   navigate: (path) => router.push(path),
 *   logError: (err) => analytics.trackError(err)
 * });
 *
 * // Later, handle an error
 * handleError({ type: 'Unauthorized' });
 */
export function createErrorHandler(deps: ErrorHandlerDeps) {
  return (error: AppError): void => {
    // Always log full error details
    const errorDetails = getErrorDetails(error);
    logger.error("Error handler", {
      error,
      type: error.type,
      message: errorDetails.message,
      details: errorDetails.details,
      originalError: errorDetails.originalError,
    });

    switch (error.type) {
      case "NetworkError":
        deps.setError(formatErrorForDisplay(error));
        deps.logError?.(error);
        break;

      case "Unauthorized":
        deps.setError("Please sign in to continue");
        deps.navigate?.("/signin");
        deps.logError?.(error);
        break;

      case "ValidationError":
        deps.setError(formatErrorForDisplay(error));
        break;

      case "NotFound":
        deps.setError(`${error.resource} not found`);
        break;

      case "AgeRestriction":
        deps.setError(
          `You must be at least ${error.minAge} years old to use this service`
        );
        break;

      case "FirebaseError": {
        // Use the mapped user-friendly message for production
        // But when showErrorDetails is true, show full details via formatErrorForDisplay
        const showDetails =
          deps.showErrorDetails ??
          import.meta.env.VITE_SHOW_ERROR_DETAILS === "true";
        if (showDetails) {
          deps.setError(formatErrorForDisplay(error, true));
        } else {
          deps.setError(mapFirebaseErrorToUserMessage(error.code));
        }
        deps.logError?.(error);
        break;
      }

      case "MissingBirthdate":
        deps.setError(
          `Unable to retrieve birthdate from ${error.provider}. Please ensure your ${error.provider} account has a birthdate set.`
        );
        break;

      case "Unexpected":
        deps.setError(formatErrorForDisplay(error));
        deps.logError?.(error);
        break;

      default:
        // Exhaustive check - TypeScript error if we miss a case
        assertNever(error);
    }
  };
}

/**
 * Legacy error handler that accepts unknown errors
 * Wraps them in Unexpected type and delegates to main handler
 *
 * Use this for gradual migration from throw-based code
 */
export function createLegacyErrorHandler(deps: ErrorHandlerDeps) {
  const handler = createErrorHandler(deps);

  return (error: unknown): void => {
    if (error && typeof error === "object" && "type" in error) {
      // Already an AppError
      handler(error as AppError);
    } else {
      // Unknown error - wrap it
      handler({ type: "Unexpected", error });
    }
  };
}
